import { memo, useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { mapUnavailableReason } from '../engine/localize';
import type { CompileResult, StitchMapSize } from '../engine/types';
import { formatNumber, formatPercent, useI18n } from '../i18n';
import { sanitizeSvg } from '../lib/sanitizeSvg';
import { sizeName } from '../lib/sizes';
import { Icon } from '../ui/Icon';
import { SizeChips } from './SizeChips';

interface View {
  x: number;
  y: number;
  k: number;
}

const MIN_K = 0.2;
const MAX_K = 10;
const clampK = (k: number) => Math.min(MAX_K, Math.max(MIN_K, k));
/** Engine map geometry (stitchcore render/svg.py): stitches span x = 20…W−20, layers are dy apart from y = H−20. */
const MARGIN_X = 20;
const MARGIN_BOTTOM = 20;
const H_EXTRA = 30;
const RULER = 22;

interface Geometry {
  w: number;
  h: number;
  /** SVG units per stitch horizontally, when the widest count is known. */
  perStitch: number | null;
  /** SVG units between two drawn rows. */
  perLayer: number | null;
}

function geometry(svg: string, entry: StitchMapSize, maxCount: number | null): Geometry | null {
  const vb = /viewBox="0 0 ([\d.]+) ([\d.]+)"/.exec(svg);
  if (!vb) return null;
  const w = Number(vb[1]);
  const h = Number(vb[2]);
  return {
    w,
    h,
    perStitch: maxCount && maxCount > 1 ? (w - 2 * MARGIN_X) / (maxCount - 1) : null,
    perLayer: entry.n_layers > 0 ? (h - H_EXTRA) / entry.n_layers : null,
  };
}

/** Widest stitch count for a size: every row when the map is complete, the cast-on when it is truncated. */
function widestCount(result: CompileResult, size: number, truncated: boolean): number | null {
  const counts: number[] = [];
  for (const [i, row] of result.rows.entries()) {
    const ps = row.per_size.find((p) => p.size === size);
    if (!ps) continue;
    if (i === 0 && typeof ps.available === 'number') counts.push(ps.available);
    if (!truncated && typeof ps.count_after === 'number') counts.push(ps.count_after);
  }
  return counts.length ? Math.max(...counts) : null;
}

/**
 * La carte des mailles, framed like a gauge swatch: the map on a cream swatch, a discreet ruler on
 * two edges (centimetres from the gauge along the width, drawn rows up the side).
 */
export const StitchMapTab = memo(function StitchMapTab({
  result,
  selectedSize,
  onSize,
}: {
  result: CompileResult;
  selectedSize: number | null;
  onSize?: (s: number | null) => void;
}) {
  const { t, lang } = useI18n();
  const size = selectedSize ?? 0;
  const entry = result.stitch_map?.per_size.find((m) => m.size === size) ?? null;
  // Only the selected size is ever rendered (maps can be large).
  const svg = useMemo(() => (entry?.svg ? sanitizeSvg(entry.svg) : null), [entry?.svg]);
  const name = sizeName(result, size, lang);
  const sizes = result.document.sizes;
  const reason = !svg ? mapUnavailableReason(entry, result) : null;
  const geo = useMemo(
    () => (svg && entry ? geometry(svg, entry, widestCount(result, size, Boolean(entry.truncated))) : null),
    [svg, entry, result, size],
  );
  const perCm = result.document.gauge?.sts_per_cm ?? null;

  return (
    <div className="flex flex-col gap-4" data-testid="map-panel">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {sizes.length > 1 && onSize ? (
          <SizeChips sizes={sizes} selected={size} onSelect={(s) => onSize(s ?? 0)} withAll={false} label={t('map.size')} testId="map-size-chips" />
        ) : (
          <span className="rounded-pill bg-heather/[0.14] px-3 py-0.5 text-xs font-bold text-fg">{t('map.showing', { size: name })}</span>
        )}
        {entry && svg && <span className="text-xs text-muted">{t('map.layers', { n: entry.n_layers })}</span>}
      </div>
      {entry?.truncated && (
        <p className="flex items-center gap-2 text-xs text-fg" data-testid="map-truncated">
          <Icon name="info" size={13} className="text-warning" />
          {t('map.truncated', { n: entry.n_layers })}
        </p>
      )}
      {!result.stitch_map ? (
        <p className="text-sm text-muted">{t('map.disabled')}</p>
      ) : svg && geo ? (
        <>
          <Swatch key={`${size}:${svg.length}`} svg={svg} geo={geo} perCm={perCm} ariaLabel={t('map.aria', { size: name })} />
          <p className="text-2xs text-subtle">
            {geo.perStitch && perCm ? t('map.rulerCmCaption', { perCm: formatNumber(lang, perCm, 2) }) : t('map.rulerStsCaption')}
          </p>
          <Legend />
        </>
      ) : (
        <p className="max-w-prose rounded-card bg-surface px-6 py-5 font-display text-md leading-relaxed text-fg" data-testid="map-unavailable" data-reason={reason ?? undefined}>
          {t(`map.reason.${reason ?? 'no_rows'}`)}
        </p>
      )}
    </div>
  );
});

function Swatch({ svg, geo, perCm, ariaLabel }: { svg: string; geo: Geometry; perCm: number | null; ariaLabel: string }) {
  const { t, lang } = useI18n();
  const outer = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<View>({ x: 0, y: 0, k: 1 });
  const [box, setBox] = useState({ w: 0, h: 0 });
  const drag = useRef<{ id: number; x: number; y: number } | null>(null);

  const fit = useCallback(() => {
    const o = outer.current;
    if (!o) return;
    const k = clampK(Math.min((o.clientWidth - 32) / geo.w, (o.clientHeight - 32) / geo.h, 2.5));
    setView({ k, x: (o.clientWidth - geo.w * k) / 2, y: (o.clientHeight - geo.h * k) / 2 });
  }, [geo.w, geo.h]);

  useEffect(() => {
    const o = outer.current;
    if (!o) return;
    const measure = () => setBox({ w: o.clientWidth, h: o.clientHeight });
    measure();
    fit();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(o);
    return () => ro.disconnect();
  }, [svg, fit]);

  const zoomAt = useCallback((factor: number, cx?: number, cy?: number) => {
    const o = outer.current;
    setView((v) => {
      const k = clampK(v.k * factor);
      const px = cx ?? (o ? o.clientWidth / 2 : 0);
      const py = cy ?? (o ? o.clientHeight / 2 : 0);
      return { k, x: px - ((px - v.x) * k) / v.k, y: py - ((py - v.y) * k) / v.k };
    });
  }, []);

  // Native listener: React's wheel handler is passive and cannot prevent page scrolling.
  useEffect(() => {
    const o = outer.current;
    if (!o) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = o.getBoundingClientRect();
      zoomAt(Math.exp(-e.deltaY * 0.0015), e.clientX - rect.left, e.clientY - rect.top);
    };
    o.addEventListener('wheel', onWheel, { passive: false });
    return () => o.removeEventListener('wheel', onWheel);
  }, [zoomAt]);

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    drag.current = { id: e.pointerId, x: e.clientX, y: e.clientY };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    drag.current = { ...d, x: e.clientX, y: e.clientY };
    setView((v) => ({ ...v, x: v.x + dx, y: v.y + dy }));
  };
  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (drag.current?.id === e.pointerId) drag.current = null;
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = 40;
    const actions: Record<string, () => void> = {
      '+': () => zoomAt(1.25),
      '=': () => zoomAt(1.25),
      '-': () => zoomAt(0.8),
      '0': fit,
      ArrowLeft: () => setView((v) => ({ ...v, x: v.x + step })),
      ArrowRight: () => setView((v) => ({ ...v, x: v.x - step })),
      ArrowUp: () => setView((v) => ({ ...v, y: v.y + step })),
      ArrowDown: () => setView((v) => ({ ...v, y: v.y - step })),
    };
    const action = actions[e.key];
    if (action) {
      e.preventDefault();
      action();
    }
  };

  const cm = Boolean(geo.perStitch && perCm);
  const unit = cm ? t('map.rulerCm') : t('map.rulerSts');
  const btn = 'inline-flex h-8 w-8 items-center justify-center rounded-control bg-surface text-muted hover:text-fg';
  return (
    <div
      className="grid overflow-hidden rounded-card bg-sunken"
      style={{ gridTemplateColumns: `${RULER}px minmax(0,1fr)`, gridTemplateRows: `${RULER}px minmax(0,1fr)` }}
      data-testid="map-swatch"
    >
      <div className="flex items-center justify-center text-[9px] font-bold uppercase tracking-wide text-subtle" aria-hidden="true">
        {unit}
      </div>
      <TopRuler view={view} width={box.w} geo={geo} perCm={cm ? perCm : null} />
      <LeftRuler view={view} height={box.h} geo={geo} />
      <div className="relative h-[22rem] min-h-0 sm:h-[26rem]">
        <div
          ref={outer}
          role="img"
          aria-label={ariaLabel}
          tabIndex={0}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onKeyDown={onKeyDown}
          className="absolute inset-0 cursor-grab touch-none overflow-hidden bg-editor active:cursor-grabbing"
          data-testid="stitch-map"
        >
          <div
            className="stitch-map absolute left-0 top-0 origin-top-left"
            style={{ width: geo.w, height: geo.h, transform: `translate(${view.x}px, ${view.y}px) scale(${view.k})` }}
            dangerouslySetInnerHTML={{ __html: svg }}
          />
        </div>
        <div className="absolute right-2 top-2 flex flex-col gap-1" data-print-hide>
          <button type="button" className={btn} onClick={() => zoomAt(1.25)} aria-label={t('map.zoomIn')} title={t('map.zoomIn')}>
            <Icon name="plus" />
          </button>
          <button type="button" className={btn} onClick={() => zoomAt(0.8)} aria-label={t('map.zoomOut')} title={t('map.zoomOut')}>
            <Icon name="minus" />
          </button>
          <button type="button" className={btn} onClick={fit} aria-label={t('map.reset')} title={t('map.reset')}>
            <Icon name="fit" />
          </button>
        </div>
        <div className="pointer-events-none absolute bottom-2 left-2 rounded-pill bg-surface px-2 py-0.5 text-2xs text-subtle tabular" data-print-hide>
          {formatPercent(lang, view.k * 100)} · {t('map.help')}
        </div>
      </div>
    </div>
  );
}

/** Ticks every `step` units (in SVG space) from `origin`, visible between 0 and `extent` on screen. */
function ticks(origin: number, perUnit: number, k: number, offset: number, extent: number, dir: 1 | -1, maxN: number) {
  const out: Array<{ pos: number; n: number }> = [];
  const px = perUnit * k;
  if (!Number.isFinite(px) || px <= 0) return out;
  const start = Math.floor((dir === 1 ? -(offset + origin * k) : offset + origin * k - extent) / px) - 1;
  const end = start + Math.ceil(extent / px) + 3;
  for (let n = Math.max(0, start); n <= Math.min(end, maxN) && out.length < 600; n++) {
    const pos = offset + (origin + dir * n * perUnit) * k;
    if (pos >= -1 && pos <= extent + 1) out.push({ pos, n });
  }
  return out;
}

function TopRuler({ view, width, geo, perCm }: { view: View; width: number; geo: Geometry; perCm: number | null }) {
  const { lang } = useI18n();
  if (!geo.perStitch || width <= 0) return <div aria-hidden="true" />;
  // One unit = 1 cm (from the gauge) or 1 stitch.
  const perUnit = perCm ? geo.perStitch * perCm : geo.perStitch;
  const px = perUnit * view.k;
  const labelEvery = perCm ? (px >= 36 ? 1 : px >= 12 ? 5 : 10) : px >= 20 ? 5 : 10;
  const minorEvery = perCm ? (px >= 5 ? 1 : 5) : px >= 4 ? 1 : 5;
  const maxN = Math.floor((geo.w - 2 * MARGIN_X) / perUnit + 1e-6);
  const list = ticks(MARGIN_X, perUnit, view.k, view.x, width, 1, maxN).filter((tk) => tk.n % minorEvery === 0);
  return (
    <svg width={width} height={RULER} className="block text-subtle" aria-hidden="true">
      {list.map(({ pos, n }) => {
        const major = n % labelEvery === 0;
        return (
          <g key={n}>
            <line x1={pos} x2={pos} y1={major ? 9 : 15} y2={RULER} stroke="currentColor" strokeOpacity={major ? 0.9 : 0.45} strokeWidth={1} />
            {major && (
              <text x={pos + 3} y={10} fontSize={9} fill="currentColor" className="tabular">
                {formatNumber(lang, n)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function LeftRuler({ view, height, geo }: { view: View; height: number; geo: Geometry }) {
  const { lang } = useI18n();
  if (!geo.perLayer || height <= 0) return <div aria-hidden="true" />;
  const origin = geo.h - MARGIN_BOTTOM;
  const list = ticks(origin, geo.perLayer, view.k, view.y, height, -1, Math.round((geo.h - H_EXTRA) / geo.perLayer) - 1);
  const px = geo.perLayer * view.k;
  const labelEvery = px >= 18 ? 1 : px >= 6 ? 5 : 10;
  return (
    <svg width={RULER} height={height} className="block text-subtle" aria-hidden="true">
      {list.map(({ pos, n }) => {
        const major = n % labelEvery === 0;
        return (
          <g key={n}>
            <line x1={major ? 10 : 15} x2={RULER} y1={pos} y2={pos} stroke="currentColor" strokeOpacity={major ? 0.9 : 0.45} strokeWidth={1} />
            {major && (
              <text x={2} y={pos - 2} fontSize={9} fill="currentColor" className="tabular">
                {formatNumber(lang, n)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

/** Mirrors the stitchcore map conventions (classes sm-k, sm-p, sm-o, sm-r, sm-l, sm-c, sm-cab, sm-flag, sm-u). */
function Legend() {
  const { t } = useI18n();
  const swatch = (body: ReactNode) => (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" className="shrink-0">
      {body}
    </svg>
  );
  const tri = (color: string) => <path d="M8 3.5l4.2 8H3.8z" style={{ fill: `var(${color})` }} />;
  const items: Array<[string, ReactNode]> = [
    [t('map.knit'), swatch(<circle cx="8" cy="8" r="3" style={{ fill: 'var(--sm-ink)' }} />)],
    [t('map.purl'), swatch(<rect x="3" y="6.6" width="10" height="2.8" style={{ fill: 'var(--sm-purl)' }} />)],
    [t('map.yo'), swatch(<circle cx="8" cy="8" r="3" style={{ fill: 'none', stroke: 'var(--sm-ink)', strokeWidth: 1.2 }} />)],
    [t('map.decRight'), swatch(tri('--sm-right'))],
    [t('map.decLeft'), swatch(tri('--sm-left'))],
    [t('map.decCentre'), swatch(tri('--sm-centre'))],
    [t('map.cable'), swatch(<path d="M4 14L12 2M12 14L4 2" style={{ fill: 'none', stroke: 'var(--sm-cable)', strokeWidth: 1.6 }} />)],
    [t('map.unworked'), swatch(<circle cx="8" cy="8" r="2.4" style={{ fill: 'var(--sm-grid)' }} />)],
    [t('map.flagged'), swatch(<rect x="0" y="4" width="16" height="8" style={{ fill: 'var(--sm-flag)' }} />)],
  ];
  return (
    <div className="flex flex-wrap items-center gap-2 text-2xs text-muted" aria-label={t('map.legend')} role="group" data-testid="map-legend">
      <span className="kicker mr-1">{t('map.legend')}</span>
      {items.map(([label, icon]) => (
        <span key={label} className="inline-flex items-center gap-1.5 rounded-pill bg-sunken py-1 pl-2 pr-3 font-semibold">
          {icon}
          {label}
        </span>
      ))}
    </div>
  );
}
